<template>
  <form class="auth-step" @submit.prevent="$emit('submit')">
    <div class="otp-back-row">
      <button type="button" class="back-link" @click="$emit('back')">
        <i class="bi bi-arrow-left"></i> Back
      </button>
    </div>

    <div class="auth-heading">
      <i class="bi bi-phone otp-icon"></i>
      <h3 class="auth-title">Verify Your Number</h3>
      <p class="auth-subtitle">We sent a 4-digit code to <strong>{{ phoneMask }}</strong></p>
    </div>

    <div v-if="errorMessage" class="auth-error">
      <i class="bi bi-exclamation-circle"></i>
      {{ errorMessage }}
    </div>

    <div v-if="mockOtpHint" class="mock-hint">
      <i class="bi bi-info-circle"></i>
      Mock OTP for testing: <strong>{{ mockOtpHint }}</strong>
    </div>

    <div class="otp-boxes" @keydown="onKeydown">
      <input
        v-for="i in 4"
        :key="i"
        ref="otpRefs"
        type="text"
        inputmode="numeric"
        maxlength="1"
        class="otp-box"
        :value="otpDigits[i - 1] || ''"
        autocomplete="one-time-code"
        @input="onBoxInput($event, i - 1)"
        @paste="onPaste"
      >
    </div>

    <div class="otp-meta">
      <span class="otp-expiry" :class="{ 'text-danger': props.otpCountdownSeconds <= 30 }">
        Code expires in {{ otpCountdownText }}
      </span>
      <span v-if="attemptsLeft > 0" class="otp-attempts">
        {{ attemptsLeft }} attempts remaining
      </span>
    </div>

    <button type="submit" class="primary-btn1 auth-submit-btn mb-20" :disabled="verifyBusy || otp.length !== 4">
      <span>{{ verifyBusy ? 'Verifying...' : 'Verify & Continue' }}</span>
    </button>

    <p class="auth-switch">
      Didn't receive code?
      <button
        type="button"
        :disabled="!canResend || resendBusy"
      @click="$emit('resend')"
      >
        {{ resendButtonText }}
      </button>
    </p>
  </form>
</template>

<script setup>
import { computed, ref, nextTick } from 'vue'

const props = defineProps({
  mode: {
    type: String,
    required: true
  },
  phoneMask: {
    type: String,
    default: ''
  },
  otp: {
    type: String,
    default: ''
  },
  attemptsLeft: {
    type: Number,
    default: 0
  },
  otpCountdownSeconds: {
    type: Number,
    default: 0
  },
  resendCountdownSeconds: {
    type: Number,
    default: 0
  },
  canResend: {
    type: Boolean,
    default: false
  },
  status: {
    type: String,
    default: 'idle'
  },
  errorMessage: {
    type: String,
    default: ''
  },
  mockOtpHint: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['submit', 'update:otp', 'resend', 'back', 'switch-mode'])

const otpRefs = ref([])

const verifyBusy = computed(
  () => props.status === 'verifying_otp' || props.status === 'signing_up'
)
const resendBusy = computed(() => props.status === 'sending_otp')

const otpDigits = computed(() => props.otp.split(''))

const otpCountdownText = computed(() => {
  const totalSeconds = Math.max(0, Number(props.otpCountdownSeconds) || 0)
  const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0')
  const seconds = String(totalSeconds % 60).padStart(2, '0')
  return `${minutes}:${seconds}`
})

const resendButtonText = computed(() => {
  if (props.canResend) {
    return resendBusy.value ? 'Resending...' : 'Resend OTP'
  }
  return `Resend in ${props.resendCountdownSeconds}s`
})

const buildOtp = (digits) => {
  const cleaned = digits.join('').replace(/\D/g, '').slice(0, 4)
  emit('update:otp', cleaned)
  return cleaned
}

const onBoxInput = (event, index) => {
  const digit = String(event.target.value || '').replace(/\D/g, '').slice(0, 1)
  const digits = [...otpDigits.value]
  digits[index] = digit
  const newOtp = buildOtp(digits)

  if (digit && index < 3) {
    nextTick(() => {
      otpRefs.value[index + 1]?.focus()
    })
  }
  // Auto-submit when 4th digit entered
  if (newOtp.length === 4 && digit) {
    nextTick(() => emit('submit'))
  }
}

const onKeydown = (event) => {
  if (event.key === 'Backspace') {
    const idx = otpRefs.value.indexOf(event.target)
    if (idx > 0 && !event.target.value) {
      event.preventDefault()
      otpRefs.value[idx - 1]?.focus()
    }
  }
}

const onPaste = (event) => {
  event.preventDefault()
  const pasted = (event.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 4)
  const digits = pasted.split('')
  buildOtp(digits)
  nextTick(() => {
    const focusIdx = Math.min(digits.length, 3)
    otpRefs.value[focusIdx]?.focus()
  })
}
</script>

<style scoped>
.otp-back-row {
  margin-bottom: 8px;
}

.back-link {
  background: none;
  border: none;
  color: #6b7280;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.back-link:hover {
  color: var(--primary-color1, #23b5ee);
}

.otp-icon {
  font-size: 48px;
  color: var(--primary-color1, #23b5ee);
  display: block;
  text-align: center;
  margin-bottom: 12px;
}

.otp-boxes {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-bottom: 16px;
}

.otp-box {
  width: 56px;
  height: 56px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  font-size: 24px;
  font-weight: 700;
  text-align: center;
  color: #111827;
  transition: border-color 0.2s;
}

.otp-box:focus {
  border-color: var(--primary-color1, #23b5ee);
  box-shadow: 0 0 0 3px rgba(35, 181, 238, 0.15);
  outline: none;
}

.otp-meta {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  margin-bottom: 20px;
  font-size: 13px;
}

.otp-expiry {
  color: #f59e0b;
  font-weight: 500;
}

.otp-expiry.text-danger {
  color: #ef4444;
}

.otp-attempts {
  color: #6b7280;
}

.mock-hint {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 10px 14px;
  color: #1e40af;
  font-size: 13px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
